import { CAMERA_TRACKS, CAMERA_TRACK_LIMITS, emptyCameraKeyframes, resolveCameraKeyframes, type CameraKeyframe, type CameraKeyframes, type CameraTrack } from '../../domain/camera-keyframes';
import { browserUuid } from '../../domain/browser-crypto';

export const cameraFrameSeconds = (seconds: number, duration: number) => Math.round(Math.max(0, Math.min(duration, seconds)) / duration * (duration * 30 - 1)) / (duration * 30 - 1) * duration;
/** Convert only on an authored edit, retaining v1 interpolation through incoming interval keys. */
export function editableCameraTracks(input?: CameraKeyframes): CameraKeyframes {
  if (!input) return emptyCameraKeyframes();
  if (input.version === 2) return input;
  const result = { ...input, version: 2 as const };
  for (const axis of CAMERA_TRACKS) result[axis] = (input[axis] ?? (axis === 'roll' ? [{ seconds: 0, value: 0, easing: 'smooth' as const }] : [])).map((key, index, keys) => ({ ...key, id: browserUuid(),
    transition: index ? 'transition' : 'cut', durationSeconds: 'interval', easing: keys[Math.max(0, index - 1)]!.easing }));
  for (const axis of CAMERA_TRACKS) if (result[axis]?.[0] && result[axis]![0]!.seconds > 0) result[axis]!.unshift({ ...result[axis]![0]!, id: browserUuid(), seconds: 0 });
  return result;
}
export function upsertAttributeKey(input: CameraKeyframes | undefined, axis: CameraTrack, seconds: number,
  value: number, baseline: number, duration: number, automatic = false): { tracks: CameraKeyframes; key: CameraKeyframe } {
  const tracks = editableCameraTracks(input), time = cameraFrameSeconds(seconds, duration);
  const keys = [...(tracks[axis] ?? [])], existing = keys.find(key => Math.abs(key.seconds - time) < .001);
  if (!keys.length && time > .001 && baseline >= CAMERA_TRACK_LIMITS[axis].min && baseline <= CAMERA_TRACK_LIMITS[axis].max) keys.push({ id: browserUuid(), seconds: 0, value: Math.max(CAMERA_TRACK_LIMITS[axis].min, Math.min(CAMERA_TRACK_LIMITS[axis].max, baseline)), transition: 'cut', easing: 'smooth', durationSeconds: 2 });
  const key: CameraKeyframe = { id: browserUuid(), transition: 'transition', durationSeconds: 2, easing: 'smooth', ...existing,
    seconds: time, value, automatic: automatic ? true : undefined };
  return { tracks: resolveCameraKeyframes({ ...tracks, [axis]: [...keys.filter(item => item !== existing), key] }, duration), key };
}

export type AttributeTiming = Partial<Pick<CameraKeyframe, 'transition' | 'durationSeconds' | 'easing'>>;
/** Timing edits retain the destination angle and the identity of an existing key. */
export function upsertAttributeTiming(input: CameraKeyframes | undefined, axis: CameraTrack, seconds: number,
  value: number, baseline: number, duration: number, timing: AttributeTiming) {
  const existing = input?.[axis]?.find(key => Math.abs(key.seconds - cameraFrameSeconds(seconds, duration)) < .001);
  const result = upsertAttributeKey(input, axis, seconds, existing?.value ?? value, baseline, duration, existing?.automatic);
  const key = { ...result.key, ...timing };
  return { tracks: resolveCameraKeyframes({ ...result.tracks,
    [axis]: result.tracks[axis]!.map(item => item.id === key.id ? key : item) }, duration), key };
}

/** Apply a two-axis gesture together so neither track replaces the other's edit. */
export function upsertOrientationKeys(input: CameraKeyframes | undefined, seconds: number,
  values: Partial<Record<'tilt' | 'roll', number>>, baseline: Record<'tilt' | 'roll', number>, duration: number) {
  let tracks = editableCameraTracks(input);
  let selected: { axis: CameraTrack; id: string } | undefined;
  for (const axis of ['tilt', 'roll'] as const) {
    const value = values[axis];
    if (value === undefined) continue;
    const result = upsertAttributeKey(tracks, axis, seconds, value, baseline[axis], duration);
    tracks = result.tracks; selected = { axis, id: result.key.id! };
  }
  return { tracks, selected };
}
